import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  TableRow,
  TableCell,
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  IconButton,
  Tooltip,
  Box,
  TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { ConditionClause, ConfigurationData, ElementDefinition } from '../../types';
import { useElementOperatorsQuery } from '../../hooks/api/useConfigurationQuery';
import { useIsMultiSelectElement } from '../../hooks/usePreselectedValues';
import MultiSelectValueInput from './MultiSelectValueInput';
import SingleValueInput from './SingleValueInput';
import AndConnector from './AndConnector';

interface ConditionTableRowProps {
  condition: ConditionClause;
  _isFirst: boolean;
  isLast: boolean;
  onUpdate: (updates: Partial<ConditionClause>) => void;
  onApply: () => void;
  onRemove: () => void;
  config?: ConfigurationData | null;
  hasUnappliedChanges?: boolean;
}

// Helper function to parse element string
const parseElementString = (element: string) => {
  if (!element) {
    return { baseElement: '', propertyName: '', signalName: '' };
  }

  const patterns = {
    userProperty: {
      regex: /app\.userProperty\['([^']+)']/,
      base: 'app.userProperty',
    },
    customSignal: {
      regex: /app\.customSignal\['([^']+)']/,
      base: 'app.customSignal',
    },
  };

  for (const key in patterns) {
    const pattern = patterns[key as keyof typeof patterns];
    const match = pattern.regex.exec(element);
    if (match?.[1]) {
      if (key === 'customSignal') {
        return { baseElement: pattern.base, propertyName: '', signalName: match[1] };
      } else {
        return { baseElement: pattern.base, propertyName: match[1], signalName: '' };
      }
    }
  }

  return { baseElement: element, propertyName: '', signalName: '' };
};

// Helper function to get element definition
const getElementDefinition = (
  config: ConfigurationData | null,
  baseElement: string,
  signalName: string
): ElementDefinition | null => {
  if (!config || !baseElement) return null;

  if (baseElement === 'app.customSignal' && signalName) {
    const customSignalDef = config.custom_signals?.[signalName];
    if (customSignalDef) {
      return customSignalDef;
    }
  }

  return config.elements[baseElement] || null;
};

// Helper function to determine apply button state
const getApplyButtonState = (
  hasUnappliedChanges: boolean,
  canApply: boolean
): { tooltip: string; color: 'warning' | 'success' | 'default'; icon: React.ReactNode } => {
  if (hasUnappliedChanges) {
    return {
      tooltip: 'Apply changes',
      color: 'warning',
      icon: <WarningIcon />,
    };
  }

  if (canApply) {
    return {
      tooltip: 'Apply condition',
      color: 'success',
      icon: <CheckIcon />,
    };
  }

  return {
    tooltip: 'Fill all fields to apply',
    color: 'default',
    icon: <CheckIcon />,
  };
};

// Helper function to create grouped elements with custom signals prioritized
const createGroupedElements = (
  config: ConfigurationData | null
): Record<string, ElementDefinition[]> => {
  if (!config) return {};

  const groups: Record<string, ElementDefinition[]> = {};

  // Add custom signals first (they are most important)
  if (config.custom_signals) {
    Object.entries(config.custom_signals).forEach(([name, element]) => {
      const category = element.category || 'other';
      if (!groups[category]) groups[category] = [];
      groups[category].push({ ...element, name, is_custom_signal: true });
    });
  }

  // Add standard Firebase elements
  Object.entries(config.elements).forEach(([name, element]) => {
    const category = element.category || 'other';
    if (!groups[category]) groups[category] = [];
    groups[category].push({ ...element, name });
  });

  // Sort elements within each category: custom signals first, then standard elements
  Object.keys(groups).forEach(category => {
    groups[category].sort((a, b) => {
      const aIsCustom = 'is_custom_signal' in a && a.is_custom_signal;
      const bIsCustom = 'is_custom_signal' in b && b.is_custom_signal;

      if (aIsCustom && !bIsCustom) return -1;
      if (!aIsCustom && bIsCustom) return 1;

      const aName = a.display_name ?? a.name;
      const bName = b.display_name ?? b.name;
      return aName.localeCompare(bName);
    });
  });

  return groups;
};

const ConditionTableRow: React.FC<ConditionTableRowProps> = ({
  condition,
  _isFirst,
  isLast,
  onUpdate,
  onApply,
  onRemove,
  config,
  hasUnappliedChanges = false,
}) => {
  // Memoized parsing of the element string to extract base element and property name
  const {
    baseElement,
    propertyName: initialPropertyName,
    signalName,
  } = useMemo(() => parseElementString(condition.element), [condition.element]);

  const [localPropertyName, setLocalPropertyName] = useState(initialPropertyName);

  // Get the full definition of the selected element
  const elementDefinition = useMemo(
    () => getElementDefinition(config ?? null, baseElement, signalName),
    [config, baseElement, signalName]
  );

  // The element name passed to API hooks
  // For custom signals, use the signal name; for others, use the base element
  const apiElementName =
    baseElement === 'app.customSignal' && signalName ? signalName : baseElement;

  const { operatorDefinitions, isLoading: operatorsLoading } = useElementOperatorsQuery(
    apiElementName,
    !!apiElementName
  );

  const isMultiSelect = useIsMultiSelectElement(apiElementName, condition.operator, config);

  // Helper function to convert condition value to appropriate format for the input
  const convertValueForInput = useCallback(
    (
      conditionValue: string | string[] | undefined,
      isMultiSelectMode: boolean
    ): string | string[] => {
      if (!conditionValue) return isMultiSelectMode ? [] : '';

      if (Array.isArray(conditionValue)) {
        return isMultiSelectMode ? conditionValue : conditionValue.join(', ');
      }

      // For multi-select, convert comma-separated string to array
      if (isMultiSelectMode && typeof conditionValue === 'string' && conditionValue.includes(',')) {
        return conditionValue
          .split(',')
          .map(v => v.trim())
          .filter(v => v.length > 0);
      }

      // For multi-select, single value should be an array
      if (isMultiSelectMode) {
        return conditionValue ? [conditionValue] : [];
      }

      return conditionValue;
    },
    []
  );

  // Local state for the value field to avoid focus loss during typing
  const [localValue, setLocalValue] = useState<string | string[]>(condition.value ?? '');
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Update local state when props change, but not if there are local unsaved changes
  useEffect(() => {
    if (!hasLocalChanges) {
      setLocalValue(convertValueForInput(condition.value, isMultiSelect));
      setLocalPropertyName(initialPropertyName);
    }
  }, [condition.value, initialPropertyName, hasLocalChanges, isMultiSelect, convertValueForInput]);

  // Clear operator when baseElement changes or becomes empty to prevent MUI errors
  useEffect(() => {
    if (!baseElement && condition.operator) {
      const updatePayload = {
        element: '',
        operator: '',
        value: '',
        isValid: false,
        validationError: undefined,
      };
      onUpdate(updatePayload);
    }
  }, [baseElement, condition.operator, onUpdate]);

  // Auto-select operator if there's only one available and none selected
  useEffect(() => {
    if (
      !operatorsLoading &&
      operatorDefinitions.length === 1 &&
      !condition.operator &&
      condition.element // Only auto-select if we have a valid element
    ) {
      const singleOperator = operatorDefinitions[0].symbol;
      const updatePayload = {
        element: condition.element, // Keep the existing element as-is
        operator: singleOperator,
        value: '',
        isValid: false,
        validationError: undefined,
      };
      onUpdate(updatePayload);
    }
  }, [operatorsLoading, operatorDefinitions, condition.operator, condition.element, onUpdate]);

  // Check if all required fields are filled to enable Apply button
  const canApply = useMemo(() => {
    const isPropertyNameRequired = elementDefinition?.type === 'user_property';
    const isPropertyNameFilled = localPropertyName.trim().length > 0;
    const isValueFilled =
      localValue &&
      (Array.isArray(localValue) ? localValue.length > 0 : localValue.trim().length > 0);

    if (isPropertyNameRequired) {
      return Boolean(baseElement && isPropertyNameFilled && condition.operator && isValueFilled);
    }

    return Boolean(baseElement && condition.operator && isValueFilled);
  }, [baseElement, localPropertyName, condition.operator, localValue, elementDefinition]);

  const handleElementChange = useCallback(
    (newElementKey: string) => {
      setLocalValue('');
      setHasLocalChanges(false);
      setLocalPropertyName(''); // Always reset property name

      // Check if this is a custom signal
      const isCustomSignal = config?.custom_signals?.[newElementKey];

      if (isCustomSignal) {
        // For custom signals, construct the full element string immediately
        const fullElement = `app.customSignal['${newElementKey}']`;
        onUpdate({
          element: fullElement,
          operator: '',
          value: '',
          isValid: false,
          validationError: undefined,
        });
      } else {
        // For regular elements, use the element name as-is
        onUpdate({
          element: newElementKey,
          operator: '',
          value: '',
          isValid: false,
          validationError: undefined,
        });
      }
    },
    [onUpdate, config]
  );

  const handleOperatorChange = useCallback(
    (operatorSymbol: string) => {
      // Preserve the current element instead of reconstructing it
      let currentElement = condition.element || baseElement;

      // Only reconstruct for user properties that need property name
      if (elementDefinition?.type === 'user_property' && localPropertyName.trim()) {
        currentElement = `${baseElement}['${localPropertyName.replace(/'/g, "\\'")}']`;
      }

      onUpdate({
        element: currentElement,
        operator: operatorSymbol,
        value: '',
        isValid: false,
        validationError: undefined,
      });
      setLocalValue('');
      setHasLocalChanges(false);
    },
    [onUpdate, baseElement, elementDefinition, localPropertyName, condition.element]
  );

  const handlePropertyNameChange = useCallback((newName: string) => {
    setLocalPropertyName(newName);
    setHasLocalChanges(true);
  }, []);

  const handleValueChange = useCallback((newValue: string | string[]) => {
    setLocalValue(newValue);
    setHasLocalChanges(true);
  }, []);

  const handleApply = useCallback(() => {
    if (!canApply) return;

    let finalElement = condition.element || baseElement;

    // Only reconstruct for user properties that need property name
    if (elementDefinition?.type === 'user_property' && localPropertyName) {
      // Escape single quotes in property name
      finalElement = `${baseElement}['${localPropertyName.replace(/'/g, "\\'")}']`;
    }

    onUpdate({
      element: finalElement,
      operator: condition.operator, // Keep existing operator
      value: localValue,
      isValid: true, // Optimistic update
      validationError: undefined,
    });
    setHasLocalChanges(false);
    onApply();
  }, [
    canApply,
    condition.element,
    baseElement,
    localPropertyName,
    localValue,
    condition.operator,
    elementDefinition,
    onUpdate,
    onApply,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && canApply) {
        event.preventDefault();
        handleApply();
      }
    },
    [canApply, handleApply]
  );

  // Get grouped elements for dropdown with custom signals prioritized
  const groupedElements = React.useMemo(() => createGroupedElements(config ?? null), [config]);

  const {
    tooltip: applyTooltip,
    color: applyIconColor,
    icon: applyIcon,
  } = getApplyButtonState(hasUnappliedChanges, canApply);

  // Calculate the correct value to show in the dropdown
  const dropdownValue = useMemo(() => {
    if (!baseElement) return '';

    // For custom signals, use the signal name directly
    if (baseElement === 'app.customSignal' && signalName) {
      return signalName; // Return the signal name (e.g., "product.name")
    }

    return baseElement;
  }, [baseElement, signalName]);

  return (
    <TableRow
      sx={{
        height: 64,
        backgroundColor: hasUnappliedChanges ? '#fff3e0' : 'inherit',
        borderLeft: hasUnappliedChanges ? '4px solid #ff9800' : 'none',
        '&:hover': {
          backgroundColor: hasUnappliedChanges ? '#ffecb3' : '#f9f9f9',
        },
      }}
      onKeyDown={handleKeyDown}
    >
      {/* Element Column */}
      <TableCell sx={{ minWidth: 350 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl fullWidth size="small">
            <Select
              value={dropdownValue}
              onChange={e => handleElementChange(e.target.value)}
              displayEmpty
              sx={{ fontSize: '14px' }}
            >
              <MenuItem value="" disabled>
                <em>Select element...</em>
              </MenuItem>
              {Object.entries(groupedElements)
                .sort(([, elementsA], [, elementsB]) => {
                  // Sort categories: ones with custom signals first
                  const aHasCustom = elementsA.some(
                    el => 'is_custom_signal' in el && el.is_custom_signal
                  );
                  const bHasCustom = elementsB.some(
                    el => 'is_custom_signal' in el && el.is_custom_signal
                  );

                  if (aHasCustom && !bHasCustom) return -1;
                  if (!aHasCustom && bHasCustom) return 1;
                  return 0; // Keep original order for categories of same type
                })
                .map(([category, elements]) => [
                  <ListSubheader key={category} sx={{ textTransform: 'capitalize' }}>
                    {category}
                  </ListSubheader>,
                  ...elements.map(element => (
                    <MenuItem key={element.name} value={element.name}>
                      <Tooltip title={element.description || ''} placement="right" arrow>
                        <span>{element.display_name ?? element.name}</span>
                      </Tooltip>
                    </MenuItem>
                  )),
                ])
                .flat()}
            </Select>
          </FormControl>
          {elementDefinition?.type === 'user_property' && (
            <TextField
              size="small"
              value={localPropertyName}
              onChange={e => handlePropertyNameChange(e.target.value)}
              placeholder="Property name..."
              sx={{ minWidth: 150 }}
              onKeyDown={handleKeyDown}
            />
          )}
        </Box>
      </TableCell>

      {/* Operator Column */}
      <TableCell sx={{ minWidth: 150 }}>
        <FormControl fullWidth size="small">
          <Select
            value={operatorDefinitions.length > 0 ? condition.operator || '' : ''}
            onChange={e => handleOperatorChange(e.target.value)}
            disabled={!condition.element || operatorsLoading}
            displayEmpty
            sx={{ fontSize: '14px' }}
          >
            <MenuItem value="" disabled>
              <em>Select operator...</em>
            </MenuItem>
            {operatorDefinitions.map(op => (
              <MenuItem key={op.symbol} value={op.symbol}>
                <Tooltip title={op.description || ''} placement="right" arrow>
                  <span>{op.display_name ?? op.symbol}</span>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>

      {/* Values Column */}
      <TableCell sx={{ minWidth: 200 }}>
        {isMultiSelect ? (
          <MultiSelectValueInput
            value={Array.isArray(localValue) ? localValue : []}
            onChange={handleValueChange}
            elementName={apiElementName}
            config={config}
            elementDefinition={elementDefinition}
            disabled={!condition.operator}
            placeholder="Select values..."
          />
        ) : (
          <SingleValueInput
            value={Array.isArray(localValue) ? localValue.join(', ') : localValue}
            onChange={handleValueChange}
            elementName={apiElementName}
            config={config}
            elementDefinition={elementDefinition}
            disabled={!condition.operator}
            placeholder="Value..."
          />
        )}
      </TableCell>

      {/* Actions Column */}
      <TableCell sx={{ width: 120 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Apply Button */}
          <Tooltip title={applyTooltip}>
            <span>
              <IconButton
                size="small"
                onClick={handleApply}
                disabled={!canApply}
                color={applyIconColor}
                sx={{
                  '&.Mui-disabled': {
                    color: '#ccc',
                  },
                }}
              >
                {applyIcon}
              </IconButton>
            </span>
          </Tooltip>

          {/* Delete Button */}
          <Tooltip title="Remove condition">
            <IconButton
              size="small"
              onClick={onRemove}
              color="error"
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                },
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>

      {/* Logic Column */}
      <TableCell sx={{ width: 80 }}>{!isLast && <AndConnector show />}</TableCell>
    </TableRow>
  );
};

export default ConditionTableRow;
